import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Slide,
} from '@mui/material';
import { Cookie as CookieIcon } from '@mui/icons-material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { hasAcceptedCookies, acceptCookies } from '../utils/cookies';
import { brandColors } from '../theme';

export const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const { navigateTo } = useAppNavigation();

  useEffect(() => {
    // Check if user has already accepted cookies
    const hasAccepted = hasAcceptedCookies();
    setShowBanner(!hasAccepted);
  }, []);

  const handleAccept = () => {
    acceptCookies();
    setShowBanner(false);
  };

  const handleLearnMore = () => {
    navigateTo('/privacy');
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          borderTop: `2px solid ${brandColors.gold}`,
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3 },
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 2, md: 3 },
            }}
          >
            {/* Cookie Icon */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 48,
              }}
            >
              <CookieIcon
                sx={{
                  fontSize: 40,
                  color: brandColors.gold,
                }}
              />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: brandColors.indigo,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CookieIcon
                  sx={{
                    display: { xs: 'inline-block', sm: 'none' },
                    fontSize: 24,
                    color: brandColors.gold,
                  }}
                />
                Cookie Notice
              </Typography>
              
              <Typography variant="body2" sx={{ mb: { xs: 2, md: 0 } }}>
                We use cookies and similar technologies to enhance your experience, maintain your session, 
                and analyze platform usage. These help us remember your preferences and improve our services. 
                By clicking "Accept Cookies," you consent to our use of cookies as described in our{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleLearnMore}
                  sx={{
                    fontWeight: 500,
                    color: brandColors.indigo,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: brandColors.gold,
                    },
                  }}
                >
                  Privacy Policy
                </Link>
                .
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  display: { xs: 'block', md: 'none' },
                }}
              >
                If you do not accept our cookie policy, please do not use this platform.
              </Typography>
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'row' },
                gap: 2,
                alignItems: 'center',
                minWidth: { md: 'auto' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  display: { xs: 'none', md: 'block' },
                  minWidth: 200,
                  textAlign: 'right',
                  pr: 2,
                }}
              >
                If you do not accept our cookie policy, please do not use this platform.
              </Typography>

              <Button
                variant="contained"
                onClick={handleAccept}
                sx={{
                  backgroundColor: brandColors.gold,
                  color: 'white',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#e09600',
                  },
                  flex: { xs: 1, sm: 'initial' },
                }}
              >
                Accept Cookies
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};